import PasswordInput from '../components/PasswordInput';
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PasswordResetApi } from '../api/PasswordResetApi';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    PasswordResetApi.validateResetToken(token)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t('passwordReset.passwordMismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('passwordReset.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      await PasswordResetApi.resetPassword({ token, newPassword });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || t('passwordReset.genericError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 40,
        width: '100%',
        maxWidth: 440,
        boxShadow: '0 4px 32px rgba(0,0,0,0.12)'
      }}>
        <h2 style={{ color: '#1976d2', marginTop: 0, marginBottom: 24 }}>
          {t('passwordReset.resetTitle')}
        </h2>

        {tokenValid === null && (
          <p style={{ color: '#666' }}>{t('common.loading')}…</p>
        )}

        {tokenValid === false && (
          <div>
            <div style={{
              color: '#d32f2f',
              background: '#ffebee',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 24,
              fontSize: 14
            }}>
              {t('passwordReset.invalidToken', 'This reset link is invalid or has already expired. Please request a new one.')}
            </div>
            <button
              onClick={() => navigate('/login')}
              style={{
                width: '100%',
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                background: '#1976d2',
                color: 'white',
                cursor: 'pointer',
                fontSize: 16
              }}
            >
              {t('passwordReset.backToLogin', 'Back to Login')}
            </button>
          </div>
        )}

        {tokenValid === true && !success && (
          <form onSubmit={handleSubmit}>
            <p style={{ color: '#666', marginTop: 0, marginBottom: 20 }}>
              {t('passwordReset.resetDescription', 'Enter your new password below.')}
            </p>
            <PasswordInput
              placeholder={t('passwordReset.newPassword')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: 16,
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 16,
                boxSizing: 'border-box'
              }}
            />
            <PasswordInput
              placeholder={t('passwordReset.confirmPassword')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: 16,
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 16,
                boxSizing: 'border-box'
              }}
            />
            {error && (
              <div style={{
                color: '#d32f2f',
                background: '#ffebee',
                padding: '8px 12px',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: 14
              }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                background: loading ? '#ccc' : '#1976d2',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 16
              }}
            >
              {loading ? t('common.loading') : t('passwordReset.resetTitle')}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: 14 }}
              >
                {t('passwordReset.backToLogin', 'Back to Login')}
              </button>
            </div>
          </form>
        )}

        {success && (
          <div>
            <div style={{
              color: '#2e7d32',
              background: '#e8f5e9',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 14
            }}>
              {t('passwordReset.resetSuccess')}
            </div>
            <p style={{ color: '#666', fontSize: 14 }}>
              {t('passwordReset.redirectingToLogin', 'Redirecting to login…')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
